import { inject, Injectable } from "@angular/core";
import { HttpService } from "../http/http.service";
import { map, Observable, BehaviorSubject, tap, filter } from "rxjs";
import { DuplicatedNPN, FormStep } from "@app/models/self-enrollment/lead";
import { environment } from "src/environments/environment";
import { Router } from "@angular/router";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ValidationService } from "../validation/validation.service";
import { ResponseAPI } from "@app/models/response-api/response-api";
import {
  CheckYourInfoData,
  CheckYourInfoSubmitResponse,
  CommittedProduction,
  LeadFromNpn,
  LeadInfoToLOB,
} from "@app/models/self-enrollment/check-your-info";
import { LineOfBusiness } from "@app/enums/lob";
import { SocketService } from "../socket-service/socket-service.service";
import Echo from "laravel-echo";

interface IVerifyNPNResponse {
  lead: LeadFromNpn;
  duplicate: boolean;
  type: DuplicatedNPN;
}

interface LeadStatusUpdate {
  lead_id: string;
  message: string;
  status: "Yes" | "No";
}

type LeadInfo = Partial<LeadInfoToLOB>;

@Injectable({
  providedIn: "root",
})
export class SelfEnrollmentService {
  private _validations: ValidationService = inject(ValidationService);
  private _socket: SocketService = inject(SocketService);
  private _http: HttpService = inject(HttpService);
  private _router: Router = inject(Router);

  public readonly echo: Echo<any> = this._socket.getSocketInstance();

  private _module = "self-enrollment";

  private _leadSubject: BehaviorSubject<LeadInfo> = new BehaviorSubject(null);
  private _videoStatusSubject: BehaviorSubject<boolean> = new BehaviorSubject(
    true
  );
  private _leadStatusSubject: BehaviorSubject<LeadStatusUpdate> =
    new BehaviorSubject(null);

  private tempNpnValue?: string;
  private _acaCommittedProduction: CommittedProduction[] = [];

  public readonly lead$ = this._leadSubject
    .asObservable()
    .pipe(filter(data => !!data));
  public readonly videoStatus$ = this._videoStatusSubject
    .asObservable();
  public readonly leadStatus$ = this._leadStatusSubject
    .asObservable()
    .pipe(filter(data => !!data));

  get lead(): LeadInfo {
    return this._leadSubject.getValue();
  }

  set lead(lead: LeadInfo) {
    this._leadSubject.next({ ...this.lead, ...lead });
  }

  get tempNpn(): string {
    return this.tempNpnValue;
  }

  set tempNpn(value: string) {
    this.tempNpnValue = value;
  }

  get acaEntered(): boolean {
    return (
      !!this.lead?.ACA_Members__c &&
      this.lead?.ACA_Members__c >= 0 &&
      !!this.lead?.CommittedProductionACA__c &&
      this.lead?.CommittedProductionACA__c >= 0 &&
      this._acaCommittedProduction.length > 0
    );
  }

  get medicareEntered(): boolean {
    return (
      !!this.lead?.Medicare_Members__c &&
      this.lead?.Medicare_Members__c >= 0 &&
      !!this.lead?.CommittedProductionMedicare__c &&
      this.lead?.CommittedProductionMedicare__c >= 0
    );
  }

  get lifeEntered(): boolean {
    return (
      !!this.lead?.Life_Members__c &&
      this.lead?.Life_Members__c >= 0 &&
      !!this.lead?.CommittedProductionLife__c &&
      this.lead?.CommittedProductionLife__c >= 0
    );
  }

  get supplementalEntered(): boolean {
    return (
      !!this.lead?.Supplementary_Members__c &&
      this.lead?.Supplementary_Members__c >= 0 &&
      !!this.lead?.CommittedProductionSupplemental__c &&
      this.lead?.CommittedProductionSupplemental__c >= 0
    );
  }

  emitVideoStatus(active: boolean) {
    this._videoStatusSubject.next(active);
  }

  verifyNPN(
    npn: string,
    g_recaptcha_response?: string
  ): Observable<IVerifyNPNResponse> {
    return this._http
      .postModule<{ data: IVerifyNPNResponse }>(`${this._module}/verify-npn`, {
        npn,
        g_recaptcha_response,
      })
      .pipe(map(res => res.data));
  }

  goToHelp() {
    (window as Window)?.open(environment.joinUrl, "_blank");
  }

  updateFormStep(status: FormStep, Id: string) {
    return this._http.postModule(`${this._module}/mark-form-step`, {
      status,
      Id,
    });
  }

  goToCheckYourInfo(npn: string) {
    this.tempNpn = npn;
    this._router.navigateByUrl("/self-enrollment/check-your-info");
  }

  getLeadByNpn(npn: string): Observable<CheckYourInfoData> {
    return this._http
      .getModule<ResponseAPI<CheckYourInfoData>>(
        `${this._module}/get-lead-info-by-npn?NPN__c=${npn}`
      )
      .pipe(map(resp => resp.data));
  }

  submitCheckYourInfo(payload): Observable<CheckYourInfoSubmitResponse> {
    return this._http
      .postModule<ResponseAPI<CheckYourInfoSubmitResponse>>(
        `${this._module}/check-your-info`,
        payload
      )
      .pipe(map(resp => resp.data));
  }

  markFormSteps(payload): Observable<Record<string, string>> {
    return this._http.postModule<Record<string, string>>(
      `${this._module}/mark-form-step`,
      payload
    );
  }

  createLead(payload): Observable<CheckYourInfoSubmitResponse> {
    return this._http
      .postModule<ResponseAPI<CheckYourInfoSubmitResponse>>(
        `${this._module}/create-lead`,
        payload
      )
      .pipe(map(resp => resp.data));
  }

  submitMaximizePotential(payload): Observable<Record<string, any>> {
    return this._http.postModule<Record<string, string>>(
      `${this._module}/update-agency-information`,
      payload
    );
  }

  saveAsAgencyRepresentative(payload): Observable<Record<string, any>> {
    return this._http.postModule<Record<string, any>>(
      `${this._module}/save-as-agency-representative`,
      payload
    );
  }

  getCheckYourInfoForm(): FormGroup {
    return new FormGroup({
      Id: new FormControl(null),
      FirstName: new FormControl(null, [
        Validators.required,
        Validators.minLength(2),
      ]),
      LastName: new FormControl(null, [
        Validators.required,
        Validators.minLength(2),
      ]),
      State: new FormControl(null, [Validators.required]),
      Phone: new FormControl(null, [
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(10),
      ]),
      Email: new FormControl(null, [
        Validators.required,
        Validators.pattern(this._validations.emailPattern),
      ]),
      Company: new FormControl(null),
    });
  }

  loadChooseYourContracts(leadId): Observable<{ cslob: string[] }> {
    return this._http
      .getModule<ResponseAPI<{ cslob: string[] }>>(
        `${this._module}/get-cslob-by-lead-id/${leadId}`
      )
      .pipe(map(resp => resp.data));
  }

  submitChooseYourContracts(payload): Observable<Record<string, any>> {
    return this._http
      .postModule<ResponseAPI<Record<string, any>>>(
        `${this._module}/choose-your-contracts`,
        payload
      )
      .pipe(map(resp => resp.data));
  }

  signAgreement(payload): Observable<Record<string, any>> {
    return this._http.postModule<ResponseAPI<Record<string, any>>>(
      `${this._module}/sign-agreement`,
      payload
    );
  }

  updateLeadStatus(payload: { Id: string }): Observable<Record<string, any>> {
    return this._http.putModule<ResponseAPI<Record<string, any>>>(
      `${this._module}/request-credentials`,
      payload
    );
  }

  getLeadInfoToLOB(npn: string): Observable<LeadInfoToLOB> {
    return this._http
      .getModule<ResponseAPI<Record<string, any>>>(
        `${this._module}/lead-info-to-lob?npn=${npn}`
      )
      .pipe(
        map(resp => resp.data?.lead as LeadInfoToLOB),
        tap(lead => {
          this.lead = lead;
          this._acaCommittedProduction =
            lead.committedProduction?.filter(
              (cp: CommittedProduction) => cp.LOB__c === LineOfBusiness.ACA
            ) ?? [];
        })
      );
  }

  checkIfExistingAgreement(leadId: string): Observable<boolean> {
    return this._http
      .getModule<ResponseAPI<any>>(
        `${this._module}/check-lead-agreement/${leadId}`
      )
      .pipe(map(resp => !!resp.data?.attachment));
  }

  goToRequestCredentials() {
    this._router.navigateByUrl("/self-enrollment/request-credentials");
  }

  listenLeadStatusUpdate() {
    this.echo
      .channel(`status-lead-update.${this.lead.Id}`)
      .listen("NotificationLeadStatusUpdate", (resp: LeadStatusUpdate) => {
        if (resp.status) {
          this._leadStatusSubject.next(resp);
        }
      });
  }
}
